import { formatDate } from '@frontend/mwm-ui'
import { useEffect, useState } from 'react'
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md'

import type { ChangeEvent, ReactElement } from 'react'

import SelectInput from '../../SelectInput'
import TextInput from '../../TextInput'

import Button from '~/components/Buttons/Button'
import Tabulation from '~/components/Tabs'
import { months } from '~/const'
import dayjs from '~/utils/dayjs'

export enum TABULATION_NAME {
  CLASSIC = 'classic',
  SINCE = 'since',
}

const tabulation = [
  {
    label: 'Classic',
    name: TABULATION_NAME.CLASSIC,
  },
  {
    label: 'Since',
    name: TABULATION_NAME.SINCE,
  },
]

interface Props {
  changeMonth: (month: number) => void
  changeYear: (year: number) => void
  date: Date
  decreaseMonth: VoidFunction
  defaultTab: TABULATION_NAME
  endDate?: string
  increaseMonth: VoidFunction
  onEndDateChange: (date?: string) => void
  onStartDateChange: (date?: string) => void
  onTabChange?: (currentTab: TABULATION_NAME) => void
  startDate?: string
  withFilters?: boolean
  withRange?: boolean
}

const DateInputHeader = ({
  changeMonth,
  changeYear,
  date,
  decreaseMonth,
  defaultTab,
  endDate: endDateProp,
  increaseMonth,
  onEndDateChange,
  onStartDateChange,
  onTabChange,
  startDate: startDateProp,
  withFilters,
  withRange,
}: Props): ReactElement => {
  /** Local state */

  const [startDate, setStartDate] = useState(startDateProp)

  const [endDate, setEndDate] = useState(endDateProp)

  const [currentTab, setCurrentTab] = useState(defaultTab)

  const years: number[] = []

  for (let i = new Date().getFullYear() - 15; i < new Date().getFullYear() + 4; i++) {
    years.push(i + 1)
  }

  const monthsOptions = months.map((month) => ({ label: month, value: month.toLowerCase() }))

  const yearsOptions = years.map((year) => ({ label: year, value: year }))

  /** Effects */

  useEffect(() => {
    if (startDateProp) {
      setStartDate(startDateProp)
    }
  }, [startDateProp])

  useEffect(() => {
    if (endDateProp) {
      setEndDate(endDateProp)
    }
  }, [endDateProp])

  /** Handlers */

  const handlePeriodChange = (period: 'start' | 'end') => {
    return (evt: ChangeEvent<HTMLInputElement>) => {
      if (period === 'start') {
        setStartDate(evt.target.value)
        onStartDateChange(evt.target.value)

        if (currentTab === TABULATION_NAME.SINCE) {
          const now = formatDate(Date.now(), 'YYYY-MM-DD')
          setEndDate(now)
          onEndDateChange(dayjs(now).toISOString())
        }
      } else if (period === 'end') {
        setEndDate(evt.target.value)
        onEndDateChange(evt.target.value)
      }
    }
  }

  const handleTabChange = (tabName: string) => {
    const newCurrentTab = tabulation.find(({ name }) => name === tabName)?.name

    if (newCurrentTab) {
      setCurrentTab(newCurrentTab)
      onTabChange && onTabChange(newCurrentTab)
    }
  }

  /** Render */

  return (
    <>
      {withFilters ? (
        <Tabulation
          className="filters-tabulation"
          currentTab={currentTab}
          onClickTab={handleTabChange}
          tabsData={tabulation}
        />
      ) : null}
      {withRange ? (
        <div className="header-date-selects">
          <TextInput
            className="input-wrapper"
            onChange={handlePeriodChange('start')}
            value={startDate}
            wrapperClassName="input-content-container"
          />
          <span className="separator" />
          {currentTab === TABULATION_NAME.SINCE ? (
            <span className="header-date-selects-text">Today</span>
          ) : (
            <TextInput
              className="input-wrapper"
              onChange={handlePeriodChange('end')}
              value={endDate}
              wrapperClassName="input-content-container"
            />
          )}
        </div>
      ) : null}
      <div className="header-container">
        <div className="selects-container">
          <SelectInput
            className="header-container-select-input header-container-select-input--month"
            isClearable={false}
            onChange={(value: unknown) => {
              changeMonth(monthsOptions.indexOf(value as { label: string; value: string }))
            }}
            options={monthsOptions}
            size="small"
            value={monthsOptions[date.getMonth()]}
          />
          <SelectInput
            className="header-container-select-input header-container-select-input--year"
            isClearable={false}
            onChange={(value: unknown) => {
              changeYear((value as { label: string; value: number }).value)
            }}
            options={yearsOptions}
            size="small"
            value={yearsOptions.find((year) => year.value === date.getFullYear())}
          />
        </div>
        <div className="month-navigation">
          <Button ghost onClick={decreaseMonth}>
            <MdKeyboardArrowLeft size={17} />
          </Button>
          <Button ghost onClick={increaseMonth}>
            <MdKeyboardArrowRight size={17} />
          </Button>
        </div>
      </div>
    </>
  )
}

export default DateInputHeader
